import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { approvePayment, fetchAllEnrollments, fetchCustomerEnrollment, fetchPayments, fetchPendingPayments, rejectPayment, triggerPaymentReminders } from "../customerSchemeApi";
import { formatDisplayDate, getEnrollmentPaymentDetails } from "../paymentPlanUtils";
import "../CustomerScheme.css";

const getMessage = (err, fallback) =>
  err?.response?.data?.msg || err?.response?.data?.message || fallback;

const isMeaningfulValue = (value) => {
  if (value === undefined || value === null) {
    return false;
  }

  const normalized = String(value).trim().toLowerCase();
  return normalized !== "" && normalized !== "null" && normalized !== "undefined";
};

const pickValue = (...values) => {
  const match = values.find((value) => isMeaningfulValue(value));
  return match === undefined ? "-" : String(match);
};

const getAny = (obj, paths = []) => {
  for (const path of paths) {
    const value = path.split(".").reduce((acc, part) => (acc ? acc[part] : undefined), obj);
    if (isMeaningfulValue(value)) {
      return value;
    }
  }
  return undefined;
};

const toCurrency = (value) => `Rs. ${Number(value || 0).toLocaleString("en-IN")}`;
const toStatus = (value) => String(value || "PENDING").toUpperCase();
const getId = (record, fallback = "") =>
  String(record?._id?.$oid || record?._id || record?.id || fallback);

const getRefId = (value) => String(value?.$oid || value || "");

const getDateObject = (value) => {
  const normalized = value?.$date || value?.iso || value?.value || value?.date || value;
  if (!normalized) {
    return null;
  }
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getInstallmentDueDate = (installment) =>
  getDateObject(
    installment?.dueDate ||
      installment?.installmentDate ||
      installment?.scheduledDate ||
      installment?.paymentDueDate ||
      installment?.date
  );

const isSettledStatus = (status) =>
  ["PAID", "APPROVED", "SUCCESS"].includes(String(status || "").toUpperCase());

const getEnrollmentDueSummary = (enrollment, today = new Date()) => {
  const paymentDetails = getEnrollmentPaymentDetails(enrollment);
  const remainingAmount = Number(paymentDetails.remainingAmount || 0);
  const cycleAmount = Number(
    paymentDetails.payableNow || paymentDetails.selectedPaymentAmount || 0
  );
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const nextMonthStart = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const installments = Array.isArray(enrollment?.installments) ? enrollment.installments : [];
  const datedOutstandingInstallments = installments
    .filter((item) => !isSettledStatus(item?.status))
    .map((item) => ({
      dueDate: getInstallmentDueDate(item),
      amount: Number(item?.amount || cycleAmount || 0),
    }))
    .filter((item) => item.dueDate);

  let thisMonthDue = datedOutstandingInstallments
    .filter((item) => item.dueDate >= monthStart && item.dueDate < nextMonthStart)
    .reduce((sum, item) => sum + item.amount, 0);
  let overdueAmount = datedOutstandingInstallments
    .filter((item) => item.dueDate < monthStart)
    .reduce((sum, item) => sum + item.amount, 0);
  let nextDueDate = datedOutstandingInstallments
    .map((item) => item.dueDate)
    .sort((a, b) => a - b)[0] || getDateObject(paymentDetails.nextDueDate);

  // Some plans expose only nextDueDate instead of dated installment rows.
  if (datedOutstandingInstallments.length === 0 && remainingAmount > 0 && nextDueDate) {
    const dueAmount = Math.min(cycleAmount || remainingAmount, remainingAmount);
    if (nextDueDate < monthStart) {
      overdueAmount = dueAmount;
    } else if (nextDueDate < nextMonthStart) {
      thisMonthDue = dueAmount;
    }
  }

  overdueAmount = Math.min(overdueAmount, remainingAmount);
  thisMonthDue = Math.min(thisMonthDue, Math.max(remainingAmount - overdueAmount, 0));

  let dueStatus = "SCHEDULE NOT AVAILABLE";
  if (remainingAmount <= 0) {
    dueStatus = "CLEAR";
    nextDueDate = null;
  } else if (overdueAmount > 0) {
    dueStatus = "OVERDUE";
  } else if (thisMonthDue > 0) {
    dueStatus = "DUE THIS MONTH";
  } else if (nextDueDate) {
    dueStatus = "UPCOMING";
  }

  return {
    totalAmount: Number(enrollment?.totalAmount || enrollment?.total || 0),
    paidAmount: Number(paymentDetails.paidAmount || 0),
    remainingAmount,
    thisMonthDue,
    overdueAmount,
    nextDueDate,
    dueStatus,
  };
};

const getDueStatusClass = (status) => {
  if (status === "CLEAR") return "customer-status customer-status-paid";
  if (status === "OVERDUE") return "customer-status customer-status-rejected";
  return "customer-status customer-status-pending";
};

const normalizeEnrollments = (raw) => {
  const source = raw?.data?.data || raw?.data || [];
  if (Array.isArray(source)) {
    return source;
  }
  if (Array.isArray(source?.enrollments)) {
    return source.enrollments;
  }
  if (source?.enrollment) {
    return [source.enrollment];
  }
  return source ? [source] : [];
};

const findMatchingEnrollment = (payment, enrollmentList = []) => {
  if (!Array.isArray(enrollmentList) || enrollmentList.length === 0) {
    return null;
  }

  const paymentEnrollmentId = getRefId(payment?.enrollmentId);
  if (paymentEnrollmentId) {
    const directMatch = enrollmentList.find((item) => getId(item) === paymentEnrollmentId);
    if (directMatch) {
      return directMatch;
    }
  }

  const paymentSchemeId = getRefId(payment?.schemeId);
  if (paymentSchemeId) {
    const schemeMatch = enrollmentList.find((item) => getRefId(item?.schemeId) === paymentSchemeId);
    if (schemeMatch) {
      return schemeMatch;
    }
  }

  const paymentCustomerId = String(payment?.customerId || "");
  if (paymentCustomerId) {
    const customerMatch = enrollmentList.find((item) => String(item?.customerId || "") === paymentCustomerId);
    if (customerMatch) {
      return customerMatch;
    }
  }

  return null;
};

const getDateValue = (payment) =>
  payment?.paymentDate || payment?.date || payment?.createdAt || payment?.updatedAt || "";

const getApplicationNumber = (payment) =>
  pickValue(
    payment?.applicationNumber,
    payment?.applicationNo,
    payment?.appNo,
    getAny(payment, [
      "enrollment.applicationNumber",
      "enrollment.applicationNo",
      "enrollment.appNo",
      "enrollmentData.applicationNumber",
      "schemeEnrollment.applicationNumber",
    ])
  );

const getProductType = (payment) =>
  pickValue(
    payment?.productType,
    payment?.schemeType,
    getAny(payment, ["enrollment.productType", "enrollmentData.productType", "schemeEnrollment.productType"])
  );

const getProductCode = (payment) =>
  pickValue(
    payment?.productCode,
    payment?.comboCode,
    payment?.schemeCode,
    getAny(payment, ["enrollment.productCode", "enrollment.comboCode", "enrollmentData.productCode", "schemeEnrollment.productCode"])
  );

const getProductDescription = (payment) =>
  pickValue(
    payment?.productDescription,
    payment?.description,
    payment?.productName,
    payment?.comboName,
    getAny(payment, [
      "enrollment.productDescription",
      "enrollment.description",
      "enrollmentData.productDescription",
      "schemeEnrollment.productDescription",
    ])
  );

const getEnrollmentStatus = (payment) =>
  pickValue(payment?.enrollmentStatus, payment?.schemeStatus, payment?.status);

const getPaymentMethod = (payment) =>
  pickValue(
    payment?.paymentMethod,
    payment?.method,
    payment?.paymentMode,
    payment?.mode,
    payment?.remarks?.includes("UPI") ? "UPI" : "",
    payment?.remarks?.includes("CASH") ? "CASH" : "",
    payment?.remarks?.includes("CARD") ? "CARD" : "",
    "MANUAL"
  ).toUpperCase();

const getSchemeLabel = (payment) => {
  const schemeName = pickValue(
    payment?.schemeName,
    payment?.productName,
    payment?.comboName,
    getAny(payment, ["scheme.name", "enrollment.schemeName", "enrollmentData.schemeName", "schemeEnrollment.schemeName"]),
    ""
  );
  const code = getProductCode(payment);
  if (schemeName !== "-" && code !== "-") {
    return `${schemeName} (${code})`;
  }
  return schemeName !== "-" ? schemeName : code;
};

const getInstallmentText = (payment) => {
  const installments = Array.isArray(payment?.installmentNumbers)
    ? payment.installmentNumbers
    : Array.isArray(payment?.installmentsCovered)
      ? payment.installmentsCovered
      : [];

  if (installments.length > 0) {
    return installments.join(", ");
  }
  return payment?.numberOfInstallments || payment?.installmentCount || payment?.installmentNumber || "-";
};
const getCustomerName = (payment) =>
  pickValue(
    payment?.customerName,
    payment?.enrollment?.customerName,
    payment?.enrollmentData?.customerName,
    payment?.schemeEnrollment?.customerName,
    payment?.name,
    payment?.customerId,
    payment?.customerMasterId
  );

const getCustomerPhone = (payment) =>
  pickValue(
    payment?.customerPhone,
    payment?.enrollment?.customerPhone,
    payment?.enrollmentData?.customerPhone,
    payment?.schemeEnrollment?.customerPhone,
    payment?.phone,
    payment?.customerMobile,
    payment?.mobile,
    payment?.customerId
  );

const getEmployeeName = (payment) =>
  pickValue(
    payment?.employeeName,
    payment?.enrollment?.employeeName,
    payment?.enrollmentData?.employeeName,
    payment?.schemeEnrollment?.employeeName,
    payment?.handledByName,
    payment?.createdByName
  );

const getEmployeePhone = (payment) =>
  pickValue(
    payment?.employeePhone,
    payment?.enrollment?.employeePhone,
    payment?.enrollmentData?.employeePhone,
    payment?.schemeEnrollment?.employeePhone,
    payment?.handledByPhone,
    payment?.createdByPhone
  );

const getEmployeeId = (payment) =>
  pickValue(
    payment?.employeeId,
    payment?.enrollment?.employeeId,
    payment?.enrollmentData?.employeeId,
    payment?.schemeEnrollment?.employeeId,
    payment?.handledBy,
    payment?.createdBy,
    payment?.employee?.employeeId
  );

const getStatusClass = (status) => {
  const normalized = toStatus(status);
  if (normalized === "APPROVED" || normalized === "PAID" || normalized === "SUCCESS") {
    return "customer-status customer-status-paid";
  }
  if (normalized === "REJECTED" || normalized === "FAILED") {
    return "customer-status customer-status-rejected";
  }
  return "customer-status customer-status-pending";
};

const getOutstandingAmount = (payment) => {
  const remaining = Number(payment?.remainingAmount || 0);
  if (remaining > 0) {
    return remaining;
  }

  const total = Number(payment?.totalAmount || 0);
  const paid = Number(payment?.paidAmount || 0);
  return Math.max(total - paid, 0);
};

const AdminPendingPayments = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [payments, setPayments] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [hasCompleteEnrollmentData, setHasCompleteEnrollmentData] = useState(true);
  const [rejectReasonById, setRejectReasonById] = useState({});
  const [actionLoadingId, setActionLoadingId] = useState("");
  const [reminderLoading, setReminderLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const [methodFilter, setMethodFilter] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedId, setExpandedId] = useState("");
  const [customerDueFilter, setCustomerDueFilter] = useState("ATTENTION");
  const [customerDueSearch, setCustomerDueSearch] = useState("");

  const loadPayments = async () => {
    try {
      setLoading(true);
      const allPaymentsResponse = await fetchPayments();
      let list = allPaymentsResponse?.data?.data || allPaymentsResponse?.data || [];

      // Keep a safety fallback in case all-payments endpoint is restricted.
      if (!Array.isArray(list) || list.length === 0) {
        const pendingResponse = await fetchPendingPayments();
        list = pendingResponse?.data?.data || pendingResponse?.data || [];
      }

      const paymentList = Array.isArray(list) ? list : [];
      let allEnrollments = [];
      let completeEnrollmentData = true;
      try {
        const enrollmentsResponse = await fetchAllEnrollments();
        allEnrollments = normalizeEnrollments(enrollmentsResponse);
      } catch (innerErr) {
        void innerErr;
        completeEnrollmentData = false;

        // Fallback: load enrollments customer-by-customer so admin table can still resolve details.
        const uniqueCustomerIds = Array.from(
          new Set(paymentList.map((item) => String(item?.customerId || "").trim()).filter(Boolean))
        );

        const fallbackResponses = await Promise.all(
          uniqueCustomerIds.map(async (customerId) => {
            try {
              const response = await fetchCustomerEnrollment(customerId);
              return normalizeEnrollments(response);
            } catch (fallbackErr) {
              void fallbackErr;
              return [];
            }
          })
        );

        allEnrollments = fallbackResponses.flat();
      }

      setEnrollments(allEnrollments);
      setHasCompleteEnrollmentData(completeEnrollmentData);

      const mergedList = paymentList.map((payment) => {
        const matchedEnrollment = findMatchingEnrollment(payment, allEnrollments);
        return matchedEnrollment
          ? {
              ...payment,
              enrollment: matchedEnrollment,
              _enrichmentSource: "MATCHED",
            }
          : {
              ...payment,
              _enrichmentSource: "NOT_MATCHED",
            };
      });

      setPayments(mergedList);
      setError("");
    } catch (err) {
      setError(getMessage(err, "Unable to fetch payments."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPayments();
  }, []);

  const paymentMethodOptions = useMemo(() => {
    const methods = new Set(
      payments
        .map((item) => getPaymentMethod(item))
        .filter(Boolean)
    );
    return ["ALL", ...Array.from(methods)];
  }, [payments]);

  const filteredPayments = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return payments.filter((payment) => {
      const status = toStatus(payment?.status);
      const method = getPaymentMethod(payment);

      const statusMatch = statusFilter === "ALL" || status === statusFilter;
      const methodMatch = methodFilter === "ALL" || method === methodFilter;

      if (!statusMatch || !methodMatch) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const searchable = [
        getApplicationNumber(payment),
        getCustomerName(payment),
        getCustomerPhone(payment),
        getEmployeeName(payment),
        getEmployeePhone(payment),
        getEmployeeId(payment),
        getSchemeLabel(payment),
        getProductType(payment),
        getProductDescription(payment),
        payment?.referenceNumber,
        payment?.remarks,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(normalizedSearch);
    });
  }, [payments, methodFilter, searchTerm, statusFilter]);

  const metrics = useMemo(() => {
    const totalAmount = payments.reduce((sum, item) => sum + Number(item?.amount || 0), 0);
    const pendingCount = payments.filter((item) => toStatus(item?.status) === "PENDING").length;
    const approvedCount = payments.filter((item) => {
      const status = toStatus(item?.status);
      return status === "APPROVED" || status === "PAID" || status === "SUCCESS";
    }).length;
    const rejectedCount = payments.filter((item) => {
      const status = toStatus(item?.status);
      return status === "REJECTED" || status === "FAILED";
    }).length;

    return {
      totalCount: payments.length,
      totalAmount,
      pendingCount,
      approvedCount,
      rejectedCount,
    };
  }, [payments]);

  const customerDueRows = useMemo(() => {
    const customerMap = new Map();

    enrollments.forEach((enrollment, index) => {
      const due = getEnrollmentDueSummary(enrollment);
      const customerName = getCustomerName(enrollment);
      const customerPhone = getCustomerPhone(enrollment);
      const customerKey = String(
        enrollment?.customerMasterId ||
          enrollment?.customerId ||
          (customerPhone !== "-" && customerPhone) ||
          (customerName !== "-" && customerName) ||
          getId(enrollment, `enrollment-${index}`)
      );
      const existing = customerMap.get(customerKey) || {
        customerKey,
        customerName,
        customerPhone,
        applicationNumbers: new Set(),
        schemes: new Set(),
        employeeNames: new Set(),
        totalAmount: 0,
        paidAmount: 0,
        remainingAmount: 0,
        thisMonthDue: 0,
        overdueAmount: 0,
        nextDueDate: null,
        dueStatuses: new Set(),
      };

      const applicationNumber = getApplicationNumber(enrollment);
      const schemeLabel = getSchemeLabel(enrollment);
      const employeeName = getEmployeeName(enrollment);
      if (applicationNumber !== "-") existing.applicationNumbers.add(applicationNumber);
      if (schemeLabel !== "-") existing.schemes.add(schemeLabel);
      if (employeeName !== "-") existing.employeeNames.add(employeeName);
      existing.totalAmount += due.totalAmount;
      existing.paidAmount += due.paidAmount;
      existing.remainingAmount += due.remainingAmount;
      existing.thisMonthDue += due.thisMonthDue;
      existing.overdueAmount += due.overdueAmount;
      existing.dueStatuses.add(due.dueStatus);
      if (due.nextDueDate && (!existing.nextDueDate || due.nextDueDate < existing.nextDueDate)) {
        existing.nextDueDate = due.nextDueDate;
      }
      customerMap.set(customerKey, existing);
    });

    return Array.from(customerMap.values())
      .map((row) => {
        let dueStatus = "SCHEDULE NOT AVAILABLE";
        if (row.dueStatuses.has("OVERDUE")) dueStatus = "OVERDUE";
        else if (row.dueStatuses.has("DUE THIS MONTH")) dueStatus = "DUE THIS MONTH";
        else if (row.remainingAmount <= 0) dueStatus = "CLEAR";
        else if (row.dueStatuses.has("UPCOMING")) dueStatus = "UPCOMING";
        return { ...row, dueStatus };
      })
      .sort((a, b) => {
        const priority = { OVERDUE: 0, "DUE THIS MONTH": 1, UPCOMING: 2, "SCHEDULE NOT AVAILABLE": 3, CLEAR: 4 };
        return (priority[a.dueStatus] ?? 5) - (priority[b.dueStatus] ?? 5) || b.remainingAmount - a.remainingAmount;
      });
  }, [enrollments]);

  const filteredCustomerDueRows = useMemo(() => {
    const normalizedSearch = customerDueSearch.trim().toLowerCase();
    return customerDueRows.filter((row) => {
      const filterMatch =
        customerDueFilter === "ALL" ||
        (customerDueFilter === "ATTENTION" && ["OVERDUE", "DUE THIS MONTH"].includes(row.dueStatus)) ||
        row.dueStatus === customerDueFilter;
      if (!filterMatch) return false;
      if (!normalizedSearch) return true;
      return [
        row.customerName,
        row.customerPhone,
        ...row.applicationNumbers,
        ...row.schemes,
        ...row.employeeNames,
      ].join(" ").toLowerCase().includes(normalizedSearch);
    });
  }, [customerDueRows, customerDueFilter, customerDueSearch]);

  const dueMetrics = useMemo(() => ({
    attentionCustomers: customerDueRows.filter((row) => ["OVERDUE", "DUE THIS MONTH"].includes(row.dueStatus)).length,
    overdueCustomers: customerDueRows.filter((row) => row.dueStatus === "OVERDUE").length,
    thisMonthDue: customerDueRows.reduce((sum, row) => sum + row.thisMonthDue, 0),
    overdueAmount: customerDueRows.reduce((sum, row) => sum + row.overdueAmount, 0),
  }), [customerDueRows]);

  const getCustomerDueExportRows = () => filteredCustomerDueRows.map((row) => ({
    Customer: row.customerName,
    Phone: row.customerPhone,
    "Employee Name": Array.from(row.employeeNames).join(", ") || "-",
    "Application Number": Array.from(row.applicationNumbers).join(", ") || "-",
    Scheme: Array.from(row.schemes).join(", ") || "-",
    "Total Amount": row.totalAmount,
    "Paid Amount": row.paidAmount,
    "Pending Balance": row.remainingAmount,
    "This Month Due": row.thisMonthDue,
    "Overdue Amount": row.overdueAmount,
    "Next Due": row.nextDueDate ? formatDisplayDate(row.nextDueDate) : "-",
    "Due Status": row.dueStatus,
  }));

  const getExportFileDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const downloadCustomerDueExcel = () => {
    const rows = getCustomerDueExportRows();
    if (rows.length === 0) return;

    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet["!cols"] = [
      { wch: 22 }, { wch: 16 }, { wch: 22 }, { wch: 22 }, { wch: 30 },
      { wch: 15 }, { wch: 15 }, { wch: 18 }, { wch: 18 }, { wch: 18 },
      { wch: 14 }, { wch: 22 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Customer Due");
    XLSX.writeFile(workbook, `customer-due-report-${getExportFileDate()}.xlsx`);
  };

  const downloadCustomerDuePdf = () => {
    const rows = getCustomerDueExportRows();
    if (rows.length === 0) return;

    const doc = new jsPDF("l", "mm", "a3");
    doc.setFontSize(16);
    doc.text("Customer Collection & Due Report", 14, 12);
    doc.setFontSize(9);
    doc.text(`Report date: ${formatDisplayDate(new Date())} | Filter: ${customerDueFilter}`, 14, 18);
    autoTable(doc, {
      startY: 23,
      head: [[
        "Customer", "Phone", "Employee", "Application", "Scheme", "Total", "Paid",
        "Pending", "This Month Due", "Overdue", "Next Due", "Status",
      ]],
      body: rows.map((row) => [
        row.Customer,
        row.Phone,
        row["Employee Name"],
        row["Application Number"],
        row.Scheme,
        `Rs. ${Number(row["Total Amount"] || 0).toLocaleString("en-IN")}`,
        `Rs. ${Number(row["Paid Amount"] || 0).toLocaleString("en-IN")}`,
        `Rs. ${Number(row["Pending Balance"] || 0).toLocaleString("en-IN")}`,
        `Rs. ${Number(row["This Month Due"] || 0).toLocaleString("en-IN")}`,
        `Rs. ${Number(row["Overdue Amount"] || 0).toLocaleString("en-IN")}`,
        row["Next Due"],
        row["Due Status"],
      ]),
      styles: { fontSize: 7, cellPadding: 2, overflow: "linebreak" },
      headStyles: { fillColor: [27, 75, 115] },
      margin: { left: 10, right: 10 },
    });
    doc.save(`customer-due-report-${getExportFileDate()}.pdf`);
  };

  const onApprove = async (paymentId) => {
    try {
      setActionLoadingId(paymentId);
      setError("");
      setSuccess("");
      const response = await approvePayment(paymentId, { approvedBy: "admin01" });
      const modeUsed = response?.data?.data?.modeUsed || response?.data?.modeUsed || "-";
      setSuccess(`Payment approved. Mode: ${modeUsed}`);
      await loadPayments();
    } catch (err) {
      setError(getMessage(err, "Approval failed."));
    } finally {
      setActionLoadingId("");
    }
  };

  const onReject = async (paymentId) => {
    const reason = rejectReasonById[paymentId] || "Reference mismatch";
    if (!reason.trim()) {
      setError("Rejection reason is required.");
      return;
    }

    try {
      setActionLoadingId(paymentId);
      setError("");
      setSuccess("");
      await rejectPayment(paymentId, { rejectedBy: "admin01", reason: reason.trim() });
      setSuccess("Payment rejected.");
      await loadPayments();
    } catch (err) {
      setError(getMessage(err, "Rejection failed."));
    } finally {
      setActionLoadingId("");
    }
  };

  const onTriggerReminder = async () => {
    try {
      setReminderLoading(true);
      setError("");
      setSuccess("");
      await triggerPaymentReminders({ triggeredBy: "admin01" });
      setSuccess("Payment reminder trigger request sent.");
    } catch (err) {
      setError(getMessage(err, "Failed to trigger reminders."));
    } finally {
      setReminderLoading(false);
    }
  };

  return (
    <div className="customer-scheme-shell">
      <div className="customer-scheme-container">
        <div className="customer-header">
          <div>
            <h2>Scheme Payment Operations</h2>
            <p className="customer-subtitle">Review customer, employee, scheme, and payment trail before approving transactions.</p>
          </div>
          <div className="customer-row">
            <button className="customer-btn customer-btn-secondary" onClick={() => navigate("/dashboard")}>Back to Dashboard</button>
            <button className="customer-btn customer-btn-secondary" onClick={loadPayments}>Refresh</button>
            <button className="customer-btn customer-btn-primary" disabled={reminderLoading} onClick={onTriggerReminder}>
              {reminderLoading ? "Triggering..." : "Trigger Reminders"}
            </button>
          </div>
        </div>

        <div className="customer-dashboard-grid" style={{ marginBottom: 16 }}>
          <div className="customer-metric">
            <div className="customer-metric-label">Total Requests</div>
            <div className="customer-metric-value">{metrics.totalCount}</div>
          </div>
          <div className="customer-metric">
            <div className="customer-metric-label">Pending</div>
            <div className="customer-metric-value">{metrics.pendingCount}</div>
          </div>
          <div className="customer-metric">
            <div className="customer-metric-label">Approved</div>
            <div className="customer-metric-value">{metrics.approvedCount}</div>
          </div>
          <div className="customer-metric">
            <div className="customer-metric-label">Rejected</div>
            <div className="customer-metric-value">{metrics.rejectedCount}</div>
          </div>
          <div className="customer-metric">
            <div className="customer-metric-label">Total Value</div>
            <div className="customer-metric-value">{toCurrency(metrics.totalAmount)}</div>
          </div>
        </div>

        <div className="customer-card customer-due-summary">
          <div className="customer-header customer-due-header">
            <div>
              <h3 className="customer-section-title">Customer-wise Collection & Due</h3>
              <p className="customer-subtitle">Customers requiring attention are shown first for the current month.</p>
            </div>
            <div className="customer-row">
              <button
                type="button"
                className="customer-btn customer-btn-secondary"
                disabled={filteredCustomerDueRows.length === 0}
                onClick={downloadCustomerDueExcel}
              >
                Download Excel
              </button>
              <button
                type="button"
                className="customer-btn customer-btn-primary"
                disabled={filteredCustomerDueRows.length === 0}
                onClick={downloadCustomerDuePdf}
              >
                Download PDF
              </button>
            </div>
          </div>

          {!hasCompleteEnrollmentData && (
            <div className="customer-error" style={{ marginBottom: 12 }}>
              Complete enrollment list could not be loaded. The due summary may contain only customers found in payment records.
            </div>
          )}

          <div className="customer-dashboard-grid customer-due-metrics">
            <div className="customer-metric">
              <div className="customer-metric-label">Customers to Contact</div>
              <div className="customer-metric-value">{dueMetrics.attentionCustomers}</div>
            </div>
            <div className="customer-metric">
              <div className="customer-metric-label">Overdue Customers</div>
              <div className="customer-metric-value">{dueMetrics.overdueCustomers}</div>
            </div>
            <div className="customer-metric">
              <div className="customer-metric-label">This Month Due</div>
              <div className="customer-metric-value">{toCurrency(dueMetrics.thisMonthDue)}</div>
            </div>
            <div className="customer-metric">
              <div className="customer-metric-label">Overdue Amount</div>
              <div className="customer-metric-value">{toCurrency(dueMetrics.overdueAmount)}</div>
            </div>
          </div>

          <div className="customer-history-filters customer-due-filters">
            <div className="customer-field">
              <label>Due Status</label>
              <select value={customerDueFilter} onChange={(e) => setCustomerDueFilter(e.target.value)}>
                <option value="ATTENTION">Due / Overdue</option>
                <option value="ALL">All Customers</option>
                <option value="OVERDUE">Overdue</option>
                <option value="DUE THIS MONTH">Due This Month</option>
                <option value="UPCOMING">Upcoming</option>
                <option value="CLEAR">Clear</option>
                <option value="SCHEDULE NOT AVAILABLE">Schedule Not Available</option>
              </select>
            </div>
            <div className="customer-field">
              <label>Search Customer</label>
              <input
                value={customerDueSearch}
                onChange={(e) => setCustomerDueSearch(e.target.value)}
                placeholder="Customer, employee, phone, application, scheme..."
              />
            </div>
          </div>

          <div className="customer-table-wrap">
            <table className="customer-table customer-due-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Employee Name</th>
                  <th>Application / Scheme</th>
                  <th>Total Amount</th>
                  <th>Paid</th>
                  <th>Pending Balance</th>
                  <th>This Month Due</th>
                  <th>Overdue</th>
                  <th>Next Due</th>
                  <th>Due Status</th>
                  <th>Contact</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomerDueRows.length === 0 ? (
                  <tr><td colSpan="12">No customers found for the selected due filter.</td></tr>
                ) : filteredCustomerDueRows.map((row) => (
                  <tr key={row.customerKey}>
                    <td>{row.customerName}</td>
                    <td>{row.customerPhone}</td>
                    <td>{Array.from(row.employeeNames).join(", ") || "-"}</td>
                    <td>
                      <div className="admin-payment-primary">{Array.from(row.applicationNumbers).join(", ") || "-"}</div>
                      <div className="admin-payment-secondary">{Array.from(row.schemes).join(", ") || "-"}</div>
                    </td>
                    <td>{toCurrency(row.totalAmount)}</td>
                    <td>{toCurrency(row.paidAmount)}</td>
                    <td>{toCurrency(row.remainingAmount)}</td>
                    <td>{toCurrency(row.thisMonthDue)}</td>
                    <td>{toCurrency(row.overdueAmount)}</td>
                    <td>{row.nextDueDate ? formatDisplayDate(row.nextDueDate) : "-"}</td>
                    <td><span className={getDueStatusClass(row.dueStatus)}>{row.dueStatus}</span></td>
                    <td>
                      {row.customerPhone !== "-" ? (
                        <a className="customer-btn customer-btn-primary customer-contact-link" href={`tel:${String(row.customerPhone).replace(/[^\d+]/g, "")}`}>
                          Call
                        </a>
                      ) : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="customer-card">
          <div className="customer-history-filters">
            <div className="customer-field">
              <label>Status</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="PENDING">Pending</option>
                <option value="ALL">All</option>
                <option value="APPROVED">Approved</option>
                <option value="PAID">Paid</option>
                <option value="SUCCESS">Success</option>
                <option value="REJECTED">Rejected</option>
                <option value="FAILED">Failed</option>
              </select>
            </div>
            <div className="customer-field">
              <label>Payment Method</label>
              <select value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)}>
                {paymentMethodOptions.map((option) => (
                  <option key={option} value={option}>
                    {option === "ALL" ? "All Methods" : option}
                  </option>
                ))}
              </select>
            </div>
            <div className="customer-field">
              <label>Search Customer / Employee / Reference</label>
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Name, phone, employee id, reference..."
              />
            </div>
          </div>
        </div>

        <div className="customer-card customer-table-wrap">
          {loading ? (
            <div>Loading payment operations...</div>
          ) : (
            <>
              {error && <div className="customer-error" style={{ marginBottom: 10 }}>{error}</div>}
              {success && <div className="customer-success" style={{ marginBottom: 10 }}>{success}</div>}

              <table className="customer-table admin-payment-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Application No</th>
                    <th>Customer Name</th>
                    <th>Customer Phone</th>
                    <th>Employee Name</th>
                    <th>Employee ID</th>
                    <th>Scheme</th>
                    <th>Plan Type</th>
                    <th>Amount</th>
                    <th>Balance</th>
                    <th>Method</th>
                    <th>Status</th>
                    <th>Reference</th>
                    <th>Due Cycles</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.length === 0 ? (
                    <tr><td colSpan="15">No payments found for selected filters.</td></tr>
                  ) : (
                    filteredPayments.map((payment, index) => {
                      const paymentId = getId(payment, `p-${index}`);
                      const isPending = toStatus(payment?.status) === "PENDING";
                      const expanded = expandedId === paymentId;
                      return (
                        <React.Fragment key={paymentId}>
                          <tr>
                            <td>{getDateValue(payment) ? new Date(getDateValue(payment)).toLocaleDateString() : "-"}</td>
                            <td>{getApplicationNumber(payment)}</td>
                            <td>{getCustomerName(payment)}</td>
                            <td>{getCustomerPhone(payment)}</td>
                            <td>{getEmployeeName(payment)}</td>
                            <td>{getEmployeeId(payment)}</td>
                            <td>{getSchemeLabel(payment)}</td>
                            <td>
                              <div className="admin-payment-primary">{getProductType(payment)}</div>
                              <div className="admin-payment-secondary">{getProductCode(payment)}</div>
                            </td>
                            <td>{toCurrency(payment?.amount)}</td>
                            <td>{toCurrency(getOutstandingAmount(payment))}</td>
                            <td>{getPaymentMethod(payment)}</td>
                            <td><span className={getStatusClass(payment?.status)}>{toStatus(payment?.status)}</span></td>
                            <td>{payment?.referenceNumber || "-"}</td>
                            <td>{getInstallmentText(payment)}</td>
                            <td>
                              <div className="customer-row">
                                <button
                                  className="customer-btn customer-btn-secondary"
                                  onClick={() => setExpandedId((prev) => (prev === paymentId ? "" : paymentId))}
                                >
                                  {expanded ? "Hide" : "Details"}
                                </button>
                                <button
                                  className="customer-btn customer-btn-primary"
                                  disabled={!isPending || actionLoadingId === paymentId}
                                  onClick={() => onApprove(paymentId)}
                                >
                                  {actionLoadingId === paymentId ? "Processing..." : "Approve"}
                                </button>
                              </div>
                              <div className="customer-row" style={{ marginTop: 8 }}>
                                <input
                                  placeholder="Reject reason"
                                  value={rejectReasonById[paymentId] || ""}
                                  onChange={(e) => setRejectReasonById((prev) => ({ ...prev, [paymentId]: e.target.value }))}
                                  className="admin-reject-input"
                                  disabled={!isPending || actionLoadingId === paymentId}
                                />
                                <button
                                  className="customer-btn customer-btn-secondary"
                                  disabled={!isPending || actionLoadingId === paymentId}
                                  onClick={() => onReject(paymentId)}
                                >
                                  Reject
                                </button>
                              </div>
                            </td>
                          </tr>

                          {expanded && (
                            <tr className="admin-payment-detail-row">
                              <td colSpan="15">
                                <div className="admin-payment-detail-grid">
                                  <div>
                                    <h4>Enrollment Details</h4>
                                    <p><strong>Application No:</strong> {getApplicationNumber(payment)}</p>
                                    <p><strong>Scheme Name:</strong> {payment?.schemeName || "-"}</p>
                                    <p><strong>Product Type:</strong> {getProductType(payment)}</p>
                                    <p><strong>Product Code:</strong> {getProductCode(payment)}</p>
                                    <p><strong>Description:</strong> {getProductDescription(payment)}</p>
                                    <p><strong>Enrollment Status:</strong> {getEnrollmentStatus(payment)}</p>
                                  </div>
                                  <div>
                                    <h4>Customer Details</h4>
                                    <p><strong>Name:</strong> {getCustomerName(payment)}</p>
                                    <p><strong>Phone:</strong> {getCustomerPhone(payment)}</p>
                                    <p><strong>Customer ID:</strong> {payment?.customerId || "-"}</p>
                                    <p><strong>Master ID:</strong> {payment?.customerMasterId || "-"}</p>
                                    <p><strong>Address:</strong> {payment?.customerAddress || payment?.address || "-"}</p>
                                  </div>
                                  <div>
                                    <h4>Employee Details</h4>
                                    <p><strong>Name:</strong> {getEmployeeName(payment)}</p>
                                    <p><strong>Phone:</strong> {getEmployeePhone(payment)}</p>
                                    <p><strong>Employee ID:</strong> {getEmployeeId(payment)}</p>
                                    <p><strong>Processed By:</strong> {payment?.approvedBy || payment?.rejectedBy || "-"}</p>
                                  </div>
                                  <div>
                                    <h4>Payment Details</h4>
                                    <p><strong>Enrollment ID:</strong> {String(payment?.enrollmentId?.$oid || payment?.enrollmentId || "-")}</p>
                                    <p><strong>Scheme ID:</strong> {String(payment?.schemeId?.$oid || payment?.schemeId || "-")}</p>
                                    <p><strong>Total Amount:</strong> {toCurrency(payment?.totalAmount)}</p>
                                    <p><strong>Amount:</strong> {toCurrency(payment?.amount)}</p>
                                    <p><strong>Scheduled Amount:</strong> {toCurrency(payment?.installmentAmount)}</p>
                                    <p><strong>Paid Amount:</strong> {toCurrency(payment?.paidAmount)}</p>
                                    <p><strong>Remaining Amount:</strong> {toCurrency(getOutstandingAmount(payment))}</p>
                                    <p><strong>Total Due Cycles:</strong> {payment?.totalInstallments || "-"}</p>
                                    <p><strong>Method:</strong> {String(payment?.paymentMethod || payment?.method || "-").toUpperCase()}</p>
                                    <p><strong>Resolved Method:</strong> {getPaymentMethod(payment)}</p>
                                    <p><strong>Status:</strong> {toStatus(payment?.status)}</p>
                                    <p><strong>Mode Used:</strong> {payment?.modeUsed || "-"}</p>
                                    <p><strong>Reference:</strong> {payment?.referenceNumber || "-"}</p>
                                    <p><strong>Due Cycles:</strong> {getInstallmentText(payment)}</p>
                                    <p><strong>Remarks:</strong> {payment?.remarks || "-"}</p>
                                    <p><strong>Reject Reason:</strong> {payment?.reason || payment?.rejectionReason || "-"}</p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPendingPayments;
